// components/NotificationSystem.tsx
import React from 'react';
import { CheckCircle, XCircle, Info } from 'lucide-react';

export interface Notification {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface NotificationSystemProps {
  notifications: Notification[];
}

const getIcon = (type: Notification['type']) => {
  switch (type) {
    case 'success':
      return <CheckCircle size={20} className="text-green-400" />;
    case 'error':
      return <XCircle size={20} className="text-red-400" />;
    case 'info':
    default:
      return <Info size={20} className="text-cyan-400" />;
  }
};

export const NotificationSystem: React.FC<NotificationSystemProps> = ({ notifications }) => {
  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="absolute top-0.5 left-1/2 -translate-x-1/2 z-[1000] w-full max-w-sm h-12 flex items-center justify-center pointer-events-none">
      <div className="relative w-full h-full flex items-center justify-center">
        {notifications.map((notification, index) => {
          const isTop = index === notifications.length - 1;
          const depth = notifications.length - 1 - index;
          return (
            <div
              key={notification.id}
              className="absolute w-full bg-slate-900/60 border border-slate-700/50 backdrop-blur-md p-2 rounded-lg shadow-2xl flex items-start gap-3 animate-in fade-in zoom-in-95 duration-300 pointer-events-auto transition-all"
              style={{
                zIndex: 1000 + index,
                transform: `translateY(${depth * 2}px) scale(${1 - depth * 0.02})`,
                opacity: isTop ? 1 : Math.max(0, 0.4 - depth * 0.1),
                filter: isTop ? 'none' : 'blur(1px)'
              }}
              role="alert"
            >
              <div className="shrink-0 mt-0.5">{getIcon(notification.type)}</div>
              <p className="text-[10px] md:text-xs text-slate-200 leading-tight truncate">{notification.message}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};